import { createSlice } from "@reduxjs/toolkit";
import {
  DerivedImportData,
  DriverInfo,
  Imports,
  ImportSummaries,
} from "../types/imports";
import { createConfig } from "../lib/encryption";

const initialState = {
  imports: {
    em: null,
    strategy: null,
    encryption: null,
  } as Imports,
  importPreviews: {
    em: null,
    strategy: null,
    encryption: null,
  } as Imports,
  importSummaries: {
    em: [],
    strategy: [],
    encryption: [],
  } as ImportSummaries,
  derivedImportData: {
    strategy: {
      driverInfo: {},
      jagDrivers: {},
    },
    encryption: null,
    em: null,
  } as DerivedImportData,
  fullViewType: null as string | null,
};

function calculateFontColour(rgbColour: string) {
  const match =
    typeof rgbColour === "string"
      ? rgbColour.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/)
      : null;
  if (!match) return "white";
  const [, r, g, b] = match.map(Number);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 125 ? "black" : "white";
}

function deriveStrategyData(strategyImport: Imports["strategy"]) {
  if (!strategyImport) {
    return { driverInfo: {}, jagDrivers: {} };
  }

  const strategyData = strategyImport.data;
  const driverInfo: Record<string, DriverInfo> = {};
  const jagDrivers: Record<string, string> = {};

  const driverCarIdIndex =
    strategyData.driverInfoHeaders.indexOf("driverCarId");
  const driverNumberIndex =
    strategyData.driverInfoHeaders.indexOf("driverNumber");

  for (const driverData of strategyData.driverInfo) {
    const driverNumberValue =
      driverNumberIndex === -1 ? null : driverData[driverNumberIndex];
    if (driverNumberValue === null || driverNumberValue === undefined) {
      continue;
    }

    const driverNumber = String(driverNumberValue);
    const driverRecord = Object.fromEntries(
      strategyData.driverInfoHeaders.map((header, headerIndex) => [
        header,
        driverData[headerIndex],
      ]),
    ) as DriverInfo;

    driverRecord.driverFontColour = calculateFontColour(
      driverRecord.driverTeamColour,
    );
    driverRecord.driverHighlightColourFontColour =
      driverRecord.driverHighlightColour
        ? calculateFontColour(driverRecord.driverHighlightColour)
        : null;
    driverRecord.driverHighlightColourFontColourAlt =
      driverRecord.driverHighlightColourAlt
        ? calculateFontColour(driverRecord.driverHighlightColourAlt)
        : null;

    driverInfo[driverNumber] = driverRecord;

    const driverCarIdValue =
      driverCarIdIndex === -1 ? null : driverData[driverCarIdIndex];
    if (driverCarIdValue !== null && driverCarIdValue !== undefined) {
      jagDrivers[String(driverCarIdValue)] = driverNumber;
    }
  }

  return { driverInfo, jagDrivers };
}

function deriveEncryptionData(encryptionImport: Imports["encryption"]) {
  if (!encryptionImport) {
    return null;
  }

  const config = createConfig(encryptionImport);
  const data = encryptionImport.data;
  const shortPriority = data.EncryptionShortPriority ?? [];
  const longPriority = data.EncryptionLongPriority ?? [];
  return {
    config,
    headers: {
      Green: config.Green.channels,
      Orange: config.Orange.channels,
    },
    priority: {
      Green: Object.fromEntries(
        config.Green.channels
          .map((channel, i) => [channel, shortPriority[i]])
          .filter(([channel]) => channel),
      ),
      Orange: Object.fromEntries(
        config.Orange.channels
          .map((channel, i) => [channel, longPriority[i]])
          .filter(([channel]) => channel),
      ),
    },
  };
}

function deriveEMData(emImport: Imports["em"]) {
  if (!emImport?.data?.NMaxSegment) {
    return null;
  }

  const emData = emImport.data;
  const numSegments = emData.NMaxSegment;
  const ecoLevel = 10;

  let totalTime = 0;
  let totalEnergy = 0;

  // Sum up total lap time and energy
  for (let seg = 1; seg <= numSegments; ++seg) {
    if (
      emData[`timeZdEdTYrgnPwrXRaceSeg${seg}Z`]?.[0]?.[ecoLevel] !== undefined
    ) {
      totalTime += emData[`timeZdEdTYrgnPwrXRaceSeg${seg}Z`][0][ecoLevel];
    }
    if (
      emData[`energyZdEdTYrgnPwrXRaceSeg${seg}Z`]?.[0]?.[ecoLevel] !== undefined
    ) {
      totalEnergy += emData[`energyZdEdTYrgnPwrXRaceSeg${seg}Z`][0][ecoLevel];
    }
  }

  if (totalTime === 0 || totalEnergy === 0) {
    return null;
  }

  // Calculate fractions
  const segmentTimeFractions: number[] = [];
  const segmentEnergyFractions: number[] = [];

  for (let seg = 1; seg <= numSegments; ++seg) {
    segmentTimeFractions.push(
      (emData[`timeZdEdTYrgnPwrXRaceSeg${seg}Z`]?.[0]?.[ecoLevel] ?? 0) /
        totalTime,
    );
    segmentEnergyFractions.push(
      (emData[`energyZdEdTYrgnPwrXRaceSeg${seg}Z`]?.[0]?.[ecoLevel] ?? 0) /
        totalEnergy,
    );
  }

  return { segmentTimeFractions, segmentEnergyFractions };
}

function deriveImportData(imports: Imports): DerivedImportData {
  return {
    strategy: deriveStrategyData(imports.strategy),
    encryption: deriveEncryptionData(imports.encryption),
    em: deriveEMData(imports.em),
  };
}

export const importsSlice = createSlice({
  name: "imports",
  initialState,
  reducers: {
    setImports: (state, action) => {
      const imports = action.payload;
      state.imports = imports;
      state.importPreviews = imports;
      state.derivedImportData = deriveImportData(imports);
      console.log("Derived Import Data:", state.derivedImportData);
    },
    setAllImports: (state, action) => {
      state.imports = action.payload;
    },
    setImportPreviews: (state, action) => {
      state.importPreviews = action.payload;
    },
    setImportPreview: (state, action) => {
      const { type, data } = action.payload;
      state.importPreviews[type as keyof Imports] = data;
    },
    setImportSummaries: (state, action) => {
      const summaries = action.payload;
      for (const summary of summaries) {
        state.importSummaries[summary.type as keyof ImportSummaries] =
          summary.summaries;
      }
    },
    setFullViewType: (state, action) => {
      state.fullViewType = action.payload;
    },
    clearFullViewType: (state) => {
      state.fullViewType = null;
    },
  },
});

export const {
  setImports,
  setImportSummaries,
  setImportPreviews,
  setImportPreview,
  setFullViewType,
  clearFullViewType,
} = importsSlice.actions;
export default importsSlice.reducer;
